import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { trackEvent } from '@/shared/lib/analytics';

interface TemplateItem {
  id: string;
  name: string;
  category: string;
  thumbnailUrl: string;
}

const EN_TEMPLATES: TemplateItem[] = [
  { id: 'developer-portfolio', name: 'Developer Portfolio', category: 'portfolio', thumbnailUrl: '/img/daniel-jackson-page-en.png' },
  { id: 'startup-landing', name: 'Startup Landing', category: 'landing', thumbnailUrl: '/img/landing-page-1.png' },
  { id: 'personal-profile', name: 'Personal Profile', category: 'profile', thumbnailUrl: '/img/alex-murphy-page-en.png' },
  { id: 'creative-portfolio', name: 'Creative Portfolio', category: 'portfolio', thumbnailUrl: '/img/olivia-dunham-page-en.png' },
  { id: 'cake-landing-page', name: 'Cake Landing Page', category: 'landing', thumbnailUrl: '/img/cake-landing-page-en.png' },
  { id: 'gradient-landing-page', name: 'Gradient Landing Page', category: 'landing', thumbnailUrl: '/img/gradient-landing-page-en.png' },
];

const AR_TEMPLATES: TemplateItem[] = [
  { id: 'developer-portfolio-ar', name: 'معرض المطور', category: 'portfolio', thumbnailUrl: '/img/daniel-jackson-page-ar.png' },
  { id: 'personal-profile-ar', name: 'الملف الشخصي', category: 'profile', thumbnailUrl: '/img/alex-murphy-page-ar.png' },
  { id: 'creative-portfolio-ar', name: 'معرض إبداعي', category: 'portfolio', thumbnailUrl: '/img/olivia-dunham-page-ar.png' },
  { id: 'product-launch-ar', name: 'إطلاق المنتج', category: 'landing', thumbnailUrl: '/img/landing-page-2-ar.png' },
  { id: 'freelancer-profile-ar', name: 'ملف المستقل', category: 'profile', thumbnailUrl: '/img/cayce-pollard-page-ar.png' },
  { id: 'gradient-profile-ar', name: 'ملف متدرج', category: 'profile', thumbnailUrl: '/img/sarah-mitchell2-page-ar.png' },
];

function ShowcaseCard({ template }: { template: TemplateItem }) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/app/try/${template.id}`}
      onClick={() =>
        trackEvent('template_previewed', {
          template_id: template.id,
          category: template.category,
          source: 'landing',
        })
      }
      className="group block rounded-xl overflow-hidden bg-card shadow-lg hover:shadow-2xl transition-shadow"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={template.thumbnailUrl}
          alt={template.name}
          loading="lazy"
          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
        />
        {/* Desktop hover overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex items-center justify-center">
          <span className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium text-sm shadow-lg">
            {t('showcase.useTemplate')}
          </span>
        </div>
      </div>
      {/* Mobile always-visible CTA */}
      <div className="p-3 sm:hidden">
        <span className="block w-full text-center py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium text-sm">
          {t('showcase.useTemplate')}
        </span>
      </div>
    </Link>
  );
}

export function TemplateShowcaseSection() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const templates = currentLanguage === 'ar' ? AR_TEMPLATES : EN_TEMPLATES;

  return (
    <section id="features" className="pt-8 pb-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl mb-4 text-[40px]">{t('features.title')}</h2>
          <p className="text-muted-foreground text-xl max-w-xl mx-auto" style={{ fontFamily: 'var(--font-body)' }}>
            {t('features.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <ShowcaseCard key={template.id} template={template} />
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/app/templates"
            onClick={() => trackEvent('cta_clicked', { location: 'features_section' })}
            className="text-emerald-400 hover:text-emerald-300 text-lg font-medium transition-colors"
          >
            {t('showcase.viewAll')}
          </Link>
        </div>
      </div>
    </section>
  );
}
